import { Create, Delete, Edit } from '@mui/icons-material'
import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Fade,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material'
import { action } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import React, { useEffect } from 'react'
import { useHistory } from 'react-router'
import {
	BaseTandemAccountModel,
	TandemAccount,
} from '../../api/clients/tandem/DTOs'
import TandemAccountsClient from '../../api/clients/tandem/TandemAccountsClient'
import { useModals } from '../../services/notifications/ModalService'
import { usePrompts } from '../../services/notifications/PromptService'
import { toastService } from '../../services/notifications/ToastService'
import AdministrationPageContainer from '../Organization/Administration/AdministrationPageContainer'
import { AdministrationPageTabs } from '../Organization/Administration/AdministrationPageTabs'
import { CreateTandemAccountDialog } from './CreateTandemAccountDialog'

type TandemAccountsDashboardProps = {
	tabLabels: string[]
	onTabChanged: (evt: React.SyntheticEvent, value: number) => void
}

export const TandemAccountsDashboard = observer(
	(props: TandemAccountsDashboardProps) => {
		const history = useHistory()
		const modalService = useModals()

		const localStore = useLocalObservable(() => ({
			accounts: [] as TandemAccount[],
		}))

		const accountsClient = new TandemAccountsClient()

		useEffect(() => {
			accountsClient
				.getAccounts()
				.then(
					action((response) => {
						localStore.accounts = response.data
					}),
				)
				.catch((e) => {
					console.error(e)
					toastService.displayToast({
						message: 'Error loading Tandem accounts',
						area: 'global',
						severity: 'error',
					})
				})
		}, [])

		const handleEditAccount = (accountId: number) => {
			history.push(`_tandem/${accountId}`)
		}

		const handleDeleteAccount = async (accountId: number) => {
			try {
				await accountsClient.deleteAccount(accountId)

				toastService.displayToast({
					message: 'Tandem account deleted',
					area: 'global',
					severity: 'success',
				})
			} catch (e) {
				console.error(e)

				toastService.displayToast({
					message: 'Error deleting Tandem account',
					area: 'global',
					severity: 'error',
				})
			}
		}

		const handleCreateAccountClicked = () => {
			modalService
				.showForm((props) => (
					<CreateTandemAccountDialog
						existingAccountNames={localStore.accounts.map((v) => v.name)}
						onConfirm={(v) => props.close({ closeResult: 'okay', value: v })}
						onCancel={() => props.close({ closeResult: 'cancel' })}
					/>
				))
				.then(async (v) => {
					if (v.closeResult === 'cancel') return

					try {
						const { data: newAccount } = await accountsClient.createAccount(
							v.value as BaseTandemAccountModel,
						)

						toastService.displayToast({
							message: 'Tandem account created',
							area: 'global',
							severity: 'success',
						})

						history.push(`_tandem/${newAccount.id}`)
					} catch (e) {
						console.error(e)

						toastService.displayToast({
							message: 'Error creating Tandem account',
							area: 'global',
							severity: 'error',
						})
					}
				})
		}

		return (
			<AdministrationPageContainer
				title="Tandem Accounts"
				actions={
					<div>
						<Button
							variant="contained"
							startIcon={<Create />}
							color="primary"
							onClick={handleCreateAccountClicked}
						>
							Add Account
						</Button>
					</div>
				}
			>
				<Box display="flex" flexDirection="column" width="100%" height="100%">
					<AdministrationPageTabs
						currentTab={1}
						onTabChanged={props.onTabChanged}
						tabLabels={props.tabLabels}
					/>
					<Divider />
					<AccountsTable
						accounts={localStore.accounts}
						onEditAccount={handleEditAccount}
						onDeleteAccount={handleDeleteAccount}
					/>
				</Box>
			</AdministrationPageContainer>
		)
	},
)

type AccountsTableProps = {
	accounts: TandemAccount[]
	onEditAccount: (accountId: number) => void
	onDeleteAccount: (accountId: number) => void
}

const AccountsTable = (props: AccountsTableProps) => {
	const promptService = usePrompts()

	return (
		<Fade in>
			<Box
				component={Paper}
				width="100%"
				marginY={3}
				height="fit-content"
				overflow="auto"
			>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Name</TableCell>
								<TableCell>Description</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{props.accounts.map((account) => (
								<TableRow key={account.id}>
									<TableCell>{account.name}</TableCell>
									<TableCell>{account.description}</TableCell>
									<TableCell align="right">
										<IconButton onClick={() => props.onEditAccount(account.id)}>
											<Edit />
										</IconButton>
										<IconButton
											size="small"
											onClick={() => {
												promptService
													.showDialog((props) => (
														<ConfirmDeleteAccountPrompt
															accountName={account.name}
															onCancel={() => props.close('cancel')}
															onConfirm={() => props.close('okay')}
														/>
													))
													.then((v) => {
														if (v === 'okay') props.onDeleteAccount(account.id)
													})
											}}
										>
											<Delete />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</Fade>
	)
}

type ConfirmDeleteAccountPromptProps = {
	accountName: string
	onConfirm: () => void
	onCancel: () => void
}

const ConfirmDeleteAccountPrompt = (props: ConfirmDeleteAccountPromptProps) => {
	return (
		<>
			<DialogTitle>Confirm Delete Tandem Account</DialogTitle>
			<DialogContent>
				<Typography>
					Are you sure that you want to delete the <b>{props.accountName}</b>{' '}
					Tandem account?
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button color="primary" onClick={props.onCancel}>
					Cancel
				</Button>
				<Button color="primary" variant="contained" onClick={props.onConfirm}>
					Confirm
				</Button>
			</DialogActions>
		</>
	)
}
